/**
 * Sanitize errors for customer-facing UI.
 * Never surface developer / infra jargon (Invalid JSON, HTTP 500, stack traces, …).
 * Duck-types ApiError (status/code) — no ApiClient import (avoids cycles).
 */
import { pickLocalizedApiMessage } from 'pi-kiosk-shared';
import i18n from '../../i18n.js';
import { isTenantInactiveError } from '../api/tenantInactive.js';
import { isMissingCredentialUnauthorized } from '../session/fetchPlatformMe.js';
import {
  isUnsafePublicApiMessage,
  publicMessageForHttpStatus,
  resolvePublicApiFailureMessage,
} from './publicApiFailureMessage.js';

export {
  isUnsafePublicApiMessage,
  looksLikeDeveloperErrorMessage,
  looksLikeNonUiLocaleEnglish,
  publicMessageForHttpStatus,
  resolvePublicApiFailureMessage,
} from './publicApiFailureMessage.js';

export function customerFacingStatusFallbackMessage(status: number | undefined): string {
  return publicMessageForHttpStatus(status);
}

export interface ToCustomerFacingErrorMessageOptions {
  readonly fallback?: string;
}

interface ApiErrorLike {
  readonly message: string;
  readonly status: number;
  readonly code?: string;
}

function asApiErrorLike(error: unknown): ApiErrorLike | null {
  if (typeof error !== 'object' || error === null) {
    return null;
  }
  const record = error as { message?: unknown; status?: unknown; code?: unknown };
  if (typeof record.status !== 'number') {
    return null;
  }
  let message = '';
  if (typeof record.message === 'string') {
    message = record.message;
  } else if (error instanceof Error) {
    message = error.message;
  }
  return {
    message,
    status: record.status,
    code: typeof record.code === 'string' ? record.code : undefined,
  };
}

function tCustomer(key: string): string {
  return i18n.t(key, { ns: 'customer' });
}

function isNetworkStyleMessage(message: string): boolean {
  const lower = message.toLowerCase();
  return (
    lower.includes('failed to fetch') ||
    lower.includes('network error') ||
    lower.includes('load failed') ||
    lower.includes('econnrefused') ||
    lower.includes('enotfound') ||
    lower.includes('etimedout') ||
    lower.includes('networkerror')
  );
}

/**
 * Resolve a public-safe message for banners, toasts, and ScreenState.
 * Prefer known ApiError.code maps; never return developer jargon.
 */
export function toCustomerFacingErrorMessage(
  error: unknown,
  options?: ToCustomerFacingErrorMessageOptions,
): string {
  const fallback =
    options?.fallback !== undefined &&
    isUnsafePublicApiMessage(options.fallback) === false
      ? options.fallback
      : tCustomer('errors.generic');

  if (isTenantInactiveError(error)) {
    return tCustomer('errors.temporaryUnavailable');
  }

  const apiError = asApiErrorLike(error);
  if (apiError !== null) {
    if (isMissingCredentialUnauthorized(apiError.code, apiError.message)) {
      return tCustomer('errors.auth.title');
    }
    return resolvePublicApiFailureMessage({
      status: apiError.status,
      code: apiError.code,
      rawMessage: apiError.message,
    });
  }

  if (error instanceof Error) {
    const localized = pickLocalizedApiMessage(
      error.message,
      i18n.resolvedLanguage ?? i18n.language,
    ).trim();
    if (isMissingCredentialUnauthorized(undefined, localized)) {
      return tCustomer('errors.auth.title');
    }
    if (isNetworkStyleMessage(localized)) {
      return tCustomer('errors.network.title');
    }
    if (localized.length > 0 && isUnsafePublicApiMessage(localized) === false) {
      return localized;
    }
    return fallback;
  }

  if (typeof error === 'string') {
    const localized = pickLocalizedApiMessage(
      error,
      i18n.resolvedLanguage ?? i18n.language,
    ).trim();
    if (isMissingCredentialUnauthorized(undefined, localized)) {
      return tCustomer('errors.auth.title');
    }
    if (isNetworkStyleMessage(localized)) {
      return tCustomer('errors.network.title');
    }
    if (localized.length > 0 && isUnsafePublicApiMessage(localized) === false) {
      return localized;
    }
    return fallback;
  }

  return fallback;
}
